/**
 * Codificação compacta do estado das camadas vetoriais no link (chave `m`).
 *
 * Formato: `<visíveis>[_p-<predef>][_<camada>-<campos>]…`
 *
 * - `<visíveis>`: códigos de 1 letra das camadas ligadas (ex.: `cme`), ou `0`
 *   se nenhuma estiver ligada. Códigos: c = nomes das cidades, m = municípios,
 *   e = estados/Amazônia Legal, t = terras indígenas, u = unidades de
 *   conservação, r = rios, h = rodovias, o = mesorregiões, i = microrregiões,
 *   n = regiões intermediárias, d = regiões imediatas, a = Regiões de
 *   Integração do Pará.
 * - `p-<predef>`: predefinição (a = alto contraste, s = discreto), omitida no padrão.
 * - `<camada>-<campos>`: só os estilos que diferem da base, separados por `.`;
 *   cada campo é `<código><valor>`: cor em hex sem `#`, booleano `1`/`0`,
 *   número inteiro já multiplicado pela escala do campo (negativo com prefixo `n`).
 *
 * Ex.: `cmet_p-a_m-cffcc00.w12_t-f0` → cidades, municípios, estados e TIs
 * ligados; alto contraste; municípios com linha #ffcc00 de 1,2 px; TIs sem
 * preenchimento. Só caracteres seguros em URL: letras, dígitos, `_`, `-`, `.`.
 */
import {
  DEFAULT_PRESET,
  STYLE_FIELDS,
  VECTOR_LAYERS,
  defaultVisibility,
  getLayerDefByCode,
  getPreset,
  getPresetByCode,
} from './catalog';
import { pruneOverride } from './styleConfig';
import type { LayerStyle, PresetId, StyleKey, StyleOverride, VectorLayerId } from './types';

export interface VectorUrlState {
  visible: Record<VectorLayerId, boolean>;
  preset: PresetId;
  overrides: Partial<Record<VectorLayerId, StyleOverride>>;
}

const FIELD_BY_CODE = new Map(
  (Object.keys(STYLE_FIELDS) as StyleKey[]).map((key) => [STYLE_FIELDS[key].code, key]),
);

function encodeValue(key: StyleKey, value: unknown): string | null {
  const spec = STYLE_FIELDS[key];
  if (spec.kind === 'color') return typeof value === 'string' ? value.replace('#', '').toLowerCase() : null;
  if (spec.kind === 'boolean') return value ? '1' : '0';
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const n = Math.round(value * spec.scale);
  return n < 0 ? `n${-n}` : String(n);
}

function decodeValue(key: StyleKey, raw: string): unknown {
  const spec = STYLE_FIELDS[key];
  if (spec.kind === 'color') return /^[0-9a-f]{6}$/i.test(raw) ? `#${raw.toLowerCase()}` : undefined;
  if (spec.kind === 'boolean') return raw === '1' ? true : raw === '0' ? false : undefined;
  const m = /^(n?)(\d{1,5})$/.exec(raw);
  if (!m) return undefined;
  const n = Number(m[2]) * (m[1] ? -1 : 1);
  return n / spec.scale;
}

/** Estado → valor do parâmetro `m`, ou null quando tudo está no padrão. */
export function encodeVectorState(state: VectorUrlState): string | null {
  const defaults = defaultVisibility();
  const visibleCodes = VECTOR_LAYERS.filter((l) => state.visible[l.id]).map((l) => l.code).join('');
  const sameVisibility = VECTOR_LAYERS.every((l) => Boolean(state.visible[l.id]) === defaults[l.id]);

  const sections: string[] = [];
  if (state.preset !== DEFAULT_PRESET) sections.push(`p-${getPreset(state.preset).code}`);

  for (const def of VECTOR_LAYERS) {
    const override = state.overrides[def.id];
    if (!override) continue;
    const pruned = pruneOverride(def.id, state.preset, override);
    if (!pruned) continue;
    // Ordem estável: a dos controles da camada.
    const entries: string[] = [];
    for (const { key } of def.controls) {
      if (!(key in pruned)) continue;
      const v = encodeValue(key, pruned[key]);
      if (v !== null) entries.push(STYLE_FIELDS[key].code + v);
    }
    if (entries.length) sections.push(`${def.code}-${entries.join('.')}`);
  }

  if (sameVisibility && sections.length === 0) return null;
  return [visibleCodes || '0', ...sections].join('_');
}

/**
 * Valor do parâmetro `m` → estado parcial. Tolera lixo: partes inválidas são
 * ignoradas; retorna null se nada puder ser aproveitado.
 */
export function decodeVectorState(value: string): Partial<VectorUrlState> | null {
  if (typeof value !== 'string') return null;
  const parts = value.trim().split('_');
  if (parts.length === 0 || parts[0] === undefined) return null;
  const out: Partial<VectorUrlState> = {};

  // 1) Camadas visíveis.
  const head = parts[0];
  if (head === '0') {
    out.visible = Object.fromEntries(VECTOR_LAYERS.map((l) => [l.id, false])) as Record<VectorLayerId, boolean>;
  } else if (head.length > 0 && !head.includes('-')) {
    // Só letras: com muitas camadas, lixo qualquer ("lixo%%%") teria letras
    // válidas por acaso. Letras desconhecidas (camadas futuras) são ignoradas.
    const defs = /^[a-z]+$/.test(head) ? [...head].map((ch) => getLayerDefByCode(ch)).filter((d) => d !== undefined) : [];
    if (defs.length > 0) {
      const visible = Object.fromEntries(VECTOR_LAYERS.map((l) => [l.id, false])) as Record<VectorLayerId, boolean>;
      for (const def of defs) visible[def.id] = true;
      out.visible = visible;
    }
  }

  // 2) Predefinição (precisa ser lida antes das personalizações).
  const sections = parts.slice(1).map((s) => {
    const i = s.indexOf('-');
    return i > 0 ? { key: s.slice(0, i), payload: s.slice(i + 1) } : null;
  });
  const presetSection = sections.find((s) => s?.key === 'p');
  const preset = presetSection ? getPresetByCode(presetSection.payload)?.id : undefined;
  if (preset) out.preset = preset;
  const effectivePreset = preset ?? DEFAULT_PRESET;

  // 3) Personalizações por camada.
  const overrides: Partial<Record<VectorLayerId, StyleOverride>> = {};
  for (const s of sections) {
    if (!s || s.key === 'p' || s.key.length !== 1) continue;
    const def = getLayerDefByCode(s.key);
    if (!def) continue;
    const raw: Partial<Record<StyleKey, unknown>> = {};
    for (const entry of s.payload.split('.')) {
      const key = FIELD_BY_CODE.get(entry.charAt(0));
      if (!key) continue;
      const v = decodeValue(key, entry.slice(1));
      if (v !== undefined) raw[key] = v;
    }
    const pruned = pruneOverride(def.id, effectivePreset, raw as Partial<LayerStyle>);
    if (pruned) overrides[def.id] = pruned;
  }
  if (Object.keys(overrides).length) out.overrides = overrides;

  return Object.keys(out).length ? out : null;
}
